import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const PosesionHero = () => {
  const scrollToForm = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const el = document.getElementById('posesion-efectiva-form');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-black overflow-hidden pt-20">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/d7c0fd06631015f2249145b001d7497c.jpg"
          alt="Posesión Efectiva"
          className="w-full h-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/50"></div>
      </div>

      {/* Decorative glows */}
      <div className="absolute top-32 right-20 w-64 h-64 bg-primary-yellow/8 rounded-full blur-3xl"></div>
      <div className="absolute bottom-32 left-10 w-48 h-48 bg-primary-yellow/6 rounded-full blur-3xl"></div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-2 bg-primary-yellow text-black text-xs font-black uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">
              <i className="ri-scales-3-line text-sm"></i>
              Servicio especializado · Chile
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-tight mb-6"
          >
            Evita errores y retrasos
            <br />
            <span className="text-primary-yellow">en tu posesión efectiva</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-xl text-gray-300 mb-4 leading-relaxed"
          >
            Gestionamos todo el proceso legal para que puedas disponer de los bienes sin rechazos ni complicaciones.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-base text-gray-400 mb-10 leading-relaxed max-w-2xl"
          >
            Un trámite mal hecho puede generar problemas legales y retrasos. Nos encargamos de todo el proceso por ti, con respaldo profesional.
          </motion.p>

          {/* Trust indicators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-wrap items-center gap-6 mb-10"
          >
            {[
              { icon: 'ri-shield-check-line', text: 'Proceso respaldado por abogados' },
              { icon: 'ri-error-warning-line', text: 'Sin rechazos ni errores' },
              { icon: 'ri-user-star-line', text: 'Servicio profesional completo' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className={`${item.icon} text-primary-yellow text-lg`}></i>
                </div>
                <span className="text-gray-300 text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.65 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#posesion-efectiva-form"
              onClick={scrollToForm}
              className="bg-primary-yellow text-black px-8 py-4 rounded-full font-bold text-base hover:bg-yellow-400 transition-all duration-300 text-center whitespace-nowrap cursor-pointer"
            >
              Evaluar mi caso
            </a>
            <a
              href="https://wa.me/56912345678?text=Hola%2C%20quiero%20consultar%20sobre%20posesión%20efectiva"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-transparent border-2 border-white/30 text-white px-8 py-4 rounded-full font-semibold text-base hover:border-white hover:bg-white/5 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-fill text-xl text-green-400"></i>
              Revisar mi situación
            </a>
          </motion.div>
        </div>

        {/* Floating stats — desktop only */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="absolute right-8 top-1/2 -translate-y-1/2 hidden xl:flex flex-col gap-4"
        >
          {[
            { num: '+300', label: 'Trámites gestionados' },
            { num: '100%', label: 'Seguimiento incluido' },
            { num: '48h', label: 'Respuesta inicial' },
          ].map((stat, idx) => (
            <div key={idx} className="bg-white/5 border border-white/10 rounded-2xl px-6 py-4 backdrop-blur-sm text-right">
              <p className="text-primary-yellow text-2xl font-black">{stat.num}</p>
              <p className="text-gray-400 text-xs mt-0.5">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2"
      >
        <span className="text-white/40 text-xs uppercase tracking-widest">Ver más</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="w-6 h-10 border-2 border-primary-yellow/40 rounded-full flex items-start justify-center p-1.5"
        >
          <div className="w-1 h-2 bg-primary-yellow rounded-full"></div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default PosesionHero;
